/**
 * Design Tokens
 *
 * Single source of truth for design system values.
 * These are used to configure the MUI theme.
 */

export const spacing = {
  0: 0,
  1: 2,    // 0.125rem
  2: 4,    // 0.25rem
  3: 6,    // 0.375rem
  4: 8,    // 0.5rem
  5: 10,   // 0.625rem
  6: 12,   // 0.75rem
  8: 16,   // 1rem
  10: 20,  // 1.25rem
  12: 24,  // 1.5rem
  16: 32,  // 2rem
  20: 40,  // 2.5rem
  24: 48,  // 3rem
  32: 64,  // 4rem
} as const;

export const radii = {
  none: 0,
  sm: 2,
  md: 4,
  lg: 6,
  xl: 8,
  '2xl': 12,
  full: 9999,
} as const;

export const typography = {
  fontFamily: {
    sans: "'Segoe UI', -apple-system, BlinkMacSystemFont, system-ui, sans-serif",
    mono: "'Cascadia Code', 'Consolas', 'SF Mono', monospace",
  },
  fontSize: {
    '2xs': '0.625rem',   // 10px
    xs: '0.6875rem',     // 11px
    sm: '0.75rem',       // 12px
    base: '0.8125rem',   // 13px
    md: '0.875rem',      // 14px
    lg: '1rem',          // 16px
    xl: '1.125rem',      // 18px
    '2xl': '1.25rem',    // 20px
    '3xl': '1.5rem',     // 24px
  },
  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  lineHeight: {
    none: 1,
    tight: 1.25,
    snug: 1.375,
    normal: 1.5,
    relaxed: 1.625,
  },
} as const;

export const palette = {
  light: {
    primary: {
      main: '#1976d2',
      light: '#42a5f5',
      dark: '#1565c0',
      darker: '#0d47a1',
      contrastText: '#ffffff',
    },
    success: {
      main: '#4caf50',
      light: '#81c784',
      dark: '#388e3c',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#ff9800',
      light: '#ffb74d',
      dark: '#f57c00',
      contrastText: 'rgba(0, 0, 0, 0.87)',
    },
    error: {
      main: '#f44336',
      light: '#e57373',
      dark: '#d32f2f',
      contrastText: '#ffffff',
    },
    info: {
      main: '#2196f3',
      light: '#64b5f6',
      dark: '#1976d2',
      contrastText: '#ffffff',
    },
    text: {
      primary: 'rgba(0, 0, 0, 0.87)',
      secondary: 'rgba(0, 0, 0, 0.6)',
      disabled: 'rgba(0, 0, 0, 0.38)',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
    divider: 'rgba(0, 0, 0, 0.12)',
    action: {
      hover: 'rgba(0, 0, 0, 0.04)',
      selected: 'rgba(0, 0, 0, 0.08)',
      disabled: 'rgba(0, 0, 0, 0.26)',
      disabledBackground: 'rgba(0, 0, 0, 0.12)',
    },
    priority: {
      p1: { main: '#c62828', bg: '#ffebee' },
      p2: { main: '#e65100', bg: '#fff3e0' },
      p3: { main: '#f57c00', bg: '#fff8e1' },
      p4: { main: '#2e7d32', bg: '#e8f5e9' },
    },
  },
  dark: {
    primary: {
      main: '#90caf9',
      light: '#a6d4fa',
      dark: '#66b2ff',
      contrastText: '#121212',
    },
    success: {
      main: '#66bb6a',
      light: '#81c784',
      dark: '#4caf50',
      contrastText: '#121212',
    },
    warning: {
      main: '#ffa726',
      light: '#ffb74d',
      dark: '#ff9800',
      contrastText: 'rgba(0, 0, 0, 0.87)',
    },
    error: {
      main: '#ef5350',
      light: '#e57373',
      dark: '#e53935',
      contrastText: '#ffffff',
    },
    info: {
      main: '#29b6f6',
      light: '#4fc3f7',
      dark: '#0288d1',
      contrastText: 'rgba(0, 0, 0, 0.87)',
    },
    text: {
      primary: 'rgba(255, 255, 255, 0.87)',
      secondary: 'rgba(255, 255, 255, 0.6)',
      disabled: 'rgba(255, 255, 255, 0.38)',
    },
    background: {
      default: '#121212',
      paper: '#1e1e1e',
    },
    divider: 'rgba(255, 255, 255, 0.12)',
    action: {
      hover: 'rgba(255, 255, 255, 0.08)',
      selected: 'rgba(255, 255, 255, 0.16)',
      disabled: 'rgba(255, 255, 255, 0.3)',
      disabledBackground: 'rgba(255, 255, 255, 0.12)',
    },
    priority: {
      p1: { main: '#ef5350', bg: 'rgba(244, 67, 54, 0.12)' },
      p2: { main: '#ffa726', bg: 'rgba(255, 167, 38, 0.12)' },
      p3: { main: '#ffb74d', bg: 'rgba(255, 167, 38, 0.12)' },
      p4: { main: '#66bb6a', bg: 'rgba(102, 187, 106, 0.12)' },
    },
  },
} as const;

export const shadows = {
  xs: '0 1px 2px rgba(0, 0, 0, 0.05)',
  sm: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  card: '0 1.6px 3.6px rgba(0, 0, 0, 0.02), 0 0.3px 0.9px rgba(0, 0, 0, 0.01)',
  cardHover: '0 3.2px 7.2px rgba(0, 0, 0, 0.03), 0 0.6px 1.8px rgba(0, 0, 0, 0.02)',
  dropdown: '0 4px 8px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.06)',
  modal: '0 8px 16px rgba(0, 0, 0, 0.14), 0 4px 8px rgba(0, 0, 0, 0.08)',
} as const;

export const transitions = {
  duration: {
    instant: 50,
    fast: 150,
    normal: 200,
    moderate: 250,
    slow: 300,
    slower: 400,
  },
  easing: {
    easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
    easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
    easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
  },
} as const;

export const zIndex = {
  base: 0,
  dropdown: 100,
  sticky: 200,
  fixed: 300,
  drawer: 400,
  modalBackdrop: 500,
  modal: 600,
  popover: 700,
  tooltip: 800,
  toast: 900,
} as const;
